import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import SearchIcon from '@material-ui/icons/Search';
import Button from '@material-ui/core/Button';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import ContentCard from '../Common/ContentCard';
import { formatDate, toExcerpt } from '../Common/useEntries';
import {
    PageHero,
    Section,
    SectionHeading,
    LoadingState,
    EmptyState,
    FilterBar,
    FilterLabel,
} from '../Common/PageLayout';

import { HRIS_JOBS, HRIS_SIGN_IN } from './hrisJobs';

const breadcrumbs = [{ label: 'Vacancies' }];

/** Days remaining until a deadline; negative once the deadline has passed. */
const daysUntil = (deadline) => {
    if (!deadline) return null;
    const end = new Date(deadline);
    if (Number.isNaN(end.getTime())) return null;
    return Math.ceil((end.getTime() - Date.now()) / 86400000);
};

/** Public list of current vacancies published by the Ministry's HRIS. */
export default function VacancyLists() {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [term, setTerm] = useState('');

    useEffect(() => {
        let active = true;
        axios
            .get(HRIS_JOBS)
            .then((response) => {
                if (!active) return;
                setJobs(Array.isArray(response.data) ? response.data : []);
                setLoading(false);
            })
            .catch(() => {
                if (!active) return;
                setError(true);
                setLoading(false);
            });
        return () => {
            active = false;
        };
    }, []);

    const filtered = useMemo(() => {
        const needle = term.trim().toLowerCase();
        if (!needle) return jobs;
        return jobs.filter((job) =>
            `${job.title || ''}`.toLowerCase().includes(needle)
        );
    }, [jobs, term]);

    return (
        <>
            <Helmet>
                <title>Vacancies | Ministry of Finance, Timor-Leste</title>
                <meta
                    name="description"
                    content="Current vacancies at the Ministry of Finance of Timor-Leste and how to submit an application through the e-recruitment system."
                />
            </Helmet>

            <PageHero
                eyebrow="Careers"
                title="Vacancies"
                description="Openings across the Ministry's directorates. Applications are submitted through the Ministry's e-recruitment system."
                breadcrumbs={breadcrumbs}
                actions={
                    <Button
                        variant="contained"
                        href={HRIS_SIGN_IN}
                        target="_blank"
                        rel="noopener noreferrer"
                        endIcon={<OpenInNewIcon />}
                        style={{ backgroundColor: '#ffffff', color: '#0e2748' }}
                    >
                        e-Recruitment sign in
                    </Button>
                }
            />

            <Section>
                <SectionHeading
                    overline="Open positions"
                    title="Current vacancies"
                    description="Each vacancy lists its closing date. Select a position to read the full terms of reference and apply."
                />

                <FilterBar resultCount={loading ? null : filtered.length} resultNoun="vacancies">
                    <div>
                        <FilterLabel>Search vacancies</FilterLabel>
                        <TextField
                            id="vacancy-search"
                            variant="outlined"
                            size="small"
                            placeholder="Position title"
                            value={term}
                            onChange={(event) => setTerm(event.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" color="disabled" />
                                    </InputAdornment>
                                ),
                            }}
                            style={{ minWidth: 280 }}
                        />
                    </div>
                </FilterBar>

                {loading && <LoadingState label="Loading vacancies" />}

                {!loading && error && (
                    <EmptyState
                        title="Vacancies could not be loaded"
                        description="The recruitment system is not responding at the moment. You can still sign in to the e-recruitment portal directly."
                        action={{
                            label: 'Open e-recruitment portal',
                            href: HRIS_SIGN_IN,
                        }}
                    />
                )}

                {!loading && !error && filtered.length === 0 && (
                    <EmptyState
                        title="No vacancies are open right now"
                        description="There are currently no published positions matching your search. New openings are announced on this page."
                    />
                )}

                {!loading && !error && filtered.length > 0 && (
                    <Grid container spacing={3}>
                        {filtered.map((job) => {
                            const remaining = daysUntil(job.deadline);
                            const closed = remaining !== null && remaining < 0;
                            return (
                                <Grid item xs={12} sm={6} md={4} key={job.id}>
                                    <ContentCard
                                        hideMedia
                                        title={job.title}
                                        description={toExcerpt(job.description?.body, 140)}
                                        meta={[
                                            job.deadline ? `Closes ${formatDate(job.deadline)}` : null,
                                            closed
                                                ? 'Closed'
                                                : remaining !== null
                                                    ? `${remaining} day${remaining === 1 ? '' : 's'} remaining`
                                                    : null,
                                        ]}
                                        to={`/jobs/${job.id}`}
                                        cta="View vacancy"
                                    />
                                </Grid>
                            );
                        })}
                    </Grid>
                )}
            </Section>
        </>
    );
}
